import React, { useState } from 'react';
import { GripVertical, MoreHorizontal, Settings2, ArrowRightLeft, Trash2, X, Pencil } from 'lucide-react';
import { widgetMeta } from './registry';

export default function WidgetFrame({ wid, widget, boardId, boards, locked, size, user, onConfig, onRemove, onMove, onOpenConfig }) {
  const meta = widgetMeta(widget.type);
  const [menu, setMenu] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const cfg = widget.config || {};
  const title = cfg.title_override || (meta.headerFrom ? meta.headerFrom(cfg) : meta.title);
  const Icon = meta.icon;
  const C = meta.C;
  const others = (boards || []).filter(b => b.id !== boardId);
  return (
    <div className="widget-tile h-full w-full flex flex-col rounded-xl bg-white/95 border border-slate-200/90 shadow-sm overflow-hidden" data-testid={`widget-${widget.type}-${wid}`}>
      <div className={`widget-drag flex items-center gap-1.5 px-2.5 h-8 shrink-0 border-b border-slate-100 bg-slate-50/80 select-none ${locked ? '' : 'cursor-grab active:cursor-grabbing'}`} data-testid={`widget-handle-${wid}`}>
        {!locked && <GripVertical className="h-3.5 w-3.5 text-slate-300" />}
        <Icon className="h-3.5 w-3.5 text-slate-500 shrink-0" />
        {renaming ? (
          <input autoFocus defaultValue={title} onBlur={e => { onConfig({ title_override: e.target.value.trim() }); setRenaming(false); }} onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); if (e.key === 'Escape') setRenaming(false); }}
            className="no-drag text-[11px] font-bold uppercase tracking-wider text-slate-700 bg-white border border-slate-300 rounded px-1 flex-1 min-w-0" data-testid={`widget-rename-${wid}`} />
        ) : (
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600 truncate flex-1" onDoubleClick={() => !locked && setRenaming(true)} title={locked ? title : `${title} — double-click to rename, drag to move`}>{title}</span>
        )}
        <div className="relative no-drag">
          <button onClick={() => setMenu(m => !m)} className="p-1 rounded text-slate-400 hover:text-slate-800 hover:bg-slate-200/70" data-testid={`widget-menu-${wid}`}><MoreHorizontal className="h-3.5 w-3.5" /></button>
          {menu && (
            <>
              <div className="fixed inset-0 z-[60]" onClick={() => { setMenu(false); setMoveOpen(false); }} />
              <div className="absolute right-0 top-7 z-[70] w-52 rounded-lg bg-white border border-slate-200 shadow-xl py-1 text-sm" data-testid={`widget-menu-list-${wid}`}>
                {meta.config && <button onClick={() => { setMenu(false); onOpenConfig(); }} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2" data-testid={`widget-configure-${wid}`}><Settings2 className="h-3.5 w-3.5" />Configure…</button>}
                <button onClick={() => { setMenu(false); setRenaming(true); }} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2" data-testid={`widget-rename-btn-${wid}`}><Pencil className="h-3.5 w-3.5" />Rename</button>
                {others.length > 0 && (
                  <div className="relative">
                    <button onClick={() => setMoveOpen(o => !o)} className="w-full text-left px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2" data-testid={`widget-move-${wid}`}><ArrowRightLeft className="h-3.5 w-3.5" />Move to board…</button>
                    {moveOpen && (
                      <div className="pl-6 pb-1">
                        {others.map(b => <button key={b.id} onClick={() => { setMenu(false); onMove(b.id); }} className="w-full text-left px-3 py-1 text-xs hover:bg-blue-50 text-blue-700 font-semibold" data-testid={`widget-move-to-${b.id}`}>→ {b.name}</button>)}
                      </div>
                    )}
                  </div>
                )}
                <button onClick={() => { setMenu(false); onRemove(); }} className="w-full text-left px-3 py-1.5 hover:bg-red-50 text-red-600 flex items-center gap-2" data-testid={`widget-remove-${wid}`}><Trash2 className="h-3.5 w-3.5" />Remove from board</button>
              </div>
            </>
          )}
        </div>
        {!locked && <button onClick={onRemove} className="no-drag p-1 rounded text-slate-300 hover:text-red-600 hover:bg-red-50" title="Remove" data-testid={`widget-x-${wid}`}><X className="h-3.5 w-3.5" /></button>}
      </div>
      <div className="widget-body flex-1 min-h-0 overflow-hidden">
        <ErrorBoundary key={widget.type}>
          <C config={cfg} onConfig={onConfig} size={size} user={user} boardId={boardId} wid={wid} />
        </ErrorBoundary>
      </div>
    </div>
  );
}

class ErrorBoundary extends React.Component {
  constructor(p) { super(p); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  render() {
    if (this.state.err) return <p className="p-3 text-xs text-red-600" data-testid="widget-crashed">This widget hit a snag. Remove and re-add it, or try again later.</p>;
    return this.props.children;
  }
}
